/**
 * API Client Error
 *
 * Custom error class for handling API errors with full type safety.
 * Provides utility methods for error type checking and form error mapping.
 */

import { ApiError, Violation, ERROR_TYPES } from '../types/api-response';

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  /** HTTP status code */
  public readonly code: number;

  /** Machine-readable error type */
  public readonly type: string;

  /** Detailed developer explanation */
  public readonly detail?: string;

  /** Validation violations */
  public readonly violations?: Violation[];

  /** Unique error instance ID */
  public readonly instance?: string;

  /** Help documentation URL */
  public readonly helpUrl?: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.code = error.code;
    this.type = error.type;
    this.detail = error.detail;
    this.violations = error.violations;
    this.instance = error.instance;
    this.helpUrl = error.helpUrl;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiClientError);
    }
  }

  // ==========================================================================
  // Type Checking Methods
  // ==========================================================================

  /** Check if this is a validation error */
  isValidationError(): boolean {
    return this.type === ERROR_TYPES.VALIDATION_ERROR;
  }

  /** Check if this is an authentication error (401) */
  isAuthError(): boolean {
    return this.code === 401;
  }

  /** Check if this is a not found error (404) */
  isNotFound(): boolean {
    return this.type === ERROR_TYPES.RESOURCE_NOT_FOUND || this.code === 404;
  }

  /** Check if this is a permission error (403) */
  isForbidden(): boolean {
    return this.type === ERROR_TYPES.INSUFFICIENT_PERMISSIONS || this.code === 403;
  }

  /** Check if this is a conflict error (409) */
  isConflict(): boolean {
    return this.type === ERROR_TYPES.CONFLICT ||
           this.type === ERROR_TYPES.RESOURCE_ALREADY_EXISTS ||
           this.code === 409;
  }

  /** Check if this is a rate limit error (429) */
  isRateLimited(): boolean {
    return this.type === ERROR_TYPES.RATE_LIMIT_EXCEEDED || this.code === 429;
  }

  /** Check if this is a server error (5xx) */
  isServerError(): boolean {
    return this.code >= 500;
  }

  // ==========================================================================
  // Violation Handling Methods
  // ==========================================================================

  /**
   * Get the violation for a specific field
   * @param fieldPath - Field path (with or without #/ prefix)
   */
  getFieldError(fieldPath: string): Violation | undefined {
    const normalizedPath = fieldPath.startsWith('#/') ? fieldPath : `#/${fieldPath}`;
    const pathWithoutPrefix = fieldPath.replace(/^#\//, '');

    return this.violations?.find(
      (v) => v.field === normalizedPath || v.field === pathWithoutPrefix || v.field === `#/${pathWithoutPrefix}`
    );
  }

  /**
   * Get all field errors as a map of field path to error message
   * Useful for form libraries like react-hook-form
   */
  getFieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {};

    this.violations?.forEach((v) => {
      // Remove #/ prefix for form library compatibility
      const field = v.field.replace(/^#\//, '').replace(/\//g, '.');
      errors[field] = v.message;
    });

    return errors;
  }

  /**
   * Get field errors in react-hook-form format
   */
  getFormErrors(): Record<string, { message: string }> {
    const errors: Record<string, { message: string }> = {};

    this.violations?.forEach((v) => {
      const field = v.field.replace(/^#\//, '').replace(/\//g, '.');
      errors[field] = { message: v.message };
    });

    return errors;
  }

  /**
   * Check if a specific field has an error
   */
  hasFieldError(fieldPath: string): boolean {
    return this.getFieldError(fieldPath) !== undefined;
  }

  /**
   * Get the number of validation violations
   */
  get violationCount(): number {
    return this.violations?.length ?? 0;
  }

  // ==========================================================================
  // Formatting Methods
  // ==========================================================================

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    return this.message;
  }

  /**
   * Get the developer detail message (if available)
   */
  getDeveloperDetail(): string | undefined {
    return this.detail;
  }

  /**
   * Format the error for logging
   */
  toLogFormat(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      type: this.type,
      message: this.message,
      detail: this.detail,
      instance: this.instance,
      violationCount: this.violationCount,
      violations: this.violations,
    };
  }

  /**
   * Convert to JSON for serialization
   */
  toJSON(): ApiError {
    return {
      code: this.code,
      type: this.type,
      message: this.message,
      detail: this.detail,
      violations: this.violations,
      instance: this.instance,
      helpUrl: this.helpUrl,
    };
  }
}

/**
 * Type guard to check if an error is an ApiClientError
 */
export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

/**
 * Create an ApiClientError from an unknown error
 * Useful for catch blocks
 */
export function toApiClientError(error: unknown): ApiClientError {
  if (error instanceof ApiClientError) {
    return error;
  }

  if (error instanceof Error) {
    return new ApiClientError({
      code: 500,
      type: ERROR_TYPES.INTERNAL_SERVER_ERROR,
      message: error.message,
    });
  }

  return new ApiClientError({
    code: 500,
    type: ERROR_TYPES.INTERNAL_SERVER_ERROR,
    message: 'An unexpected error occurred',
  });
}
